'use client';

import { useRef, useEffect, useLayoutEffect, useState, type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import type { CatalogItem, CatalogType } from '@/lib/services/catalog/catalog-types';
import { CatalogCard } from '../catalog-card';

// Add a style block to hide scrollbars and prevent direct horizontal scrolling
const hideScrollbarStyles = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  
  .prevent-horizontal-scroll {
    overscroll-behavior-x: none;
    -ms-overflow-style: none;
    scrollbar-width: none;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    
    /* Snap scroll özellikleri */
    scroll-snap-type: x mandatory;
    scroll-padding: 0 20px;
  }
  
  .gallery-item {
    scroll-snap-align: center;
  }
`;

interface GalleryWallProps {
  artworks: CatalogItem[];
  onView?: (artwork: CatalogItem) => void;
  onEnlarge: (artwork: CatalogItem) => void;
  catalogType?: CatalogType;
}

export function GalleryWall({ artworks, onView, onEnlarge, catalogType: _catalogType = 'all' }: GalleryWallProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const wall = scrollContainerRef.current;
    if (!wall) return undefined;
    const catalog = wall.closest('#catalog-scroll-container');
    const controls = catalog?.querySelector('[data-catalog-controls]');
    const measure = () => {
      const trackOffset = wall.closest('#about-series-track')?.scrollTop ?? 0;
      const top = wall.getBoundingClientRect().top + trackOffset + (catalog?.scrollTop ?? 0);
      const bottom = Math.min(window.innerHeight, catalog ? catalog.getBoundingClientRect().bottom + trackOffset : window.innerHeight, controls?.getBoundingClientRect().top ?? window.innerHeight);
      setAvailableHeight(Math.max(1, Math.floor(bottom - top - 12)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (catalog) observer.observe(catalog);
    if (controls) observer.observe(controls);
    window.addEventListener('resize', measure);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  // Set up handlers to convert vertical scroll to horizontal scroll and block direct horizontal interactions
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return undefined;

    // Daha basit bir wheel handler
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();

      // Smooth scroll için item genişliğini baz alarak scroll yapma
      const itemWidth = scrollContainer.firstElementChild?.clientWidth || 300;
      const direction = e.deltaY > 0 ? 1 : -1;

      scrollContainer.scrollBy({
        left: itemWidth * direction,
        behavior: 'smooth',
      });
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div className="gallery-wall overflow-y-hidden" style={{ '--gallery-wall-height': availableHeight === null ? 'calc(100dvh - 260px)' : `${availableHeight}px` } as CSSProperties}>
      <style dangerouslySetInnerHTML={{ __html: hideScrollbarStyles }} />
      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto pt-0 snap-x snap-mandatory hide-scrollbar prevent-horizontal-scroll"
        id="gallery-wall-scroll-container"
        style={{
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
          gap: '20px', // Reduced spacing between items
          height: 'var(--gallery-wall-height)', // The measured space above the search controls.
        }}
      >
        {artworks.map((artwork, index) => (
          <motion.div
            key={`${artwork.id}-${index}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: 0.6,
              delay: index * 0.08, // Staggered delay based on position
              ease: [0.25, 0.1, 0.25, 1.0], // Custom easing curve
            }}
            style={{
              scrollSnapAlign: 'center', // Make all images snap to center
              scrollSnapStop: 'always', // Ensure it always stops at each snap point
            }}
            className="flex-none h-full"
          >
            <CatalogCard
              key={`${artwork.id}-${index}`}
              artwork={artwork}
              onView={onView}
              onEnlarge={onEnlarge}
              galleryWall={true}
              index={index}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
